// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — onglet « Trésorerie » d'une organisation (DÉMONSTRATION) :
//   • créances (paginées, filtre d'état ; solde toujours calculé, jamais saisi) ;
//   • paiements et décaissements (préparer → signer → contresigner → exécuter ; annulation motivée) ;
//   • comptes de trésorerie : solde du livre, chèques (liste, chéquier, annulation ou opposition), arrêtés de caisse
//     (billetage) et rapprochements bancaires, avec leur contrôle par une autre personne (identifiants listés).
//   • mise en place (étape 5) : création d'un compte de trésorerie de démonstration (banque ou caisse) ; ?vue=comptes.
// Routes : /api/dispositifs/finance (README du module) ; tout POST avec Idempotency-Key.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Ban, Banknote, BookCheck, ChevronRight, Landmark, ListChecks, Plus, Scale, Wallet } from 'lucide-react';
import { Badge, Bouton, BandeauInfo } from '../../design/composants';
import { toArray, libelle, aujourdhuiDakar } from './api';
import { Champ, Etat, PaginationDs, Fenetre, ErreurServeur } from './commun';
import {
  useSocial, nouvelleCle, cleARenouveler, francs, dateLongue, moisLong, periodeCourante, periodeDecalee, lireMontant, ETATS_CREANCE, ETATS_PAIEMENT, MODES_PAIEMENT, OBJETS_PAIEMENT, COUPURES, STATUTS_CHEQUE,
} from './apiSocial';
import {
  erreurSociale, useLectureSociale, EtatsLecture, FiltreEtats, FenetreActe, Montant, BandeauComptaDemo, Infos, usePieceDepot, ChampPiece, ChampMotif, motifValide,
} from './communSocial';
import { BadgeSimulation } from './EcheanciersCommun';
import { ChoixCompteMode, choixInitial, lireChoix, paiementComplet, champsPaiement } from './ChoixPaiement';
import { FenetreCompteTresorerie } from './MiseEnPlaceFinance';
import { LigneSuivi } from '../../components/suivi/SuiviDemande'; // prochain acteur : `suivi` des lignes (lecture)

const ORIGINES = { PRET: 'Prêt social', INSCRIPTION: 'Campagne', COTISATION: 'Cotisation', PARTS: 'Parts sociales' };

// ─── Créances ──────────────────────────────────────────────────────────────────────────────────
function Creances({ organisation }) {
  const api = useSocial();
  const naviguer = useNavigate();
  const [etat, setEtat] = useState('');
  const [page, setPage] = useState(1);
  const liste = useLectureSociale(() => api.creances(organisation.id, { page, taille: 25, etat }), [api, organisation.id, page, etat]);
  const elements = toArray(liste.donnees);
  const ouvrir = (c) => naviguer(`/dispositifs/${organisation.id}/tresorerie/creances/${c.creance_id || c.id}`);
  return (
    <>
      <div className="rh-outils"><FiltreEtats table={ETATS_CREANCE} codes={Object.keys(ETATS_CREANCE)} valeur={etat} onChange={(c) => { setEtat(c); setPage(1); }} /></div>
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={liste} Icone={Wallet} estVide={!elements.length} titreVide={etat ? 'Aucune créance dans cet état' : 'Aucune créance'}
          vide={etat ? 'Choisissez un autre état.' : 'Une créance naît au décaissement d\'un prêt ou à la remise d\'une campagne. Une aide n\'en crée jamais.'}>
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Créance</th><th>Origine</th><th style={{ textAlign: 'right' }}>Montant initial</th><th style={{ textAlign: 'right' }}>Restant dû</th><th>État</th><th aria-label="Ouvrir" /></tr></thead>
              <tbody>
                {elements.map((c) => (
                  <tr key={c.creance_id || c.id} className="rh-ligne" tabIndex={0} onClick={() => ouvrir(c)} onKeyDown={(e) => { if (e.key === 'Enter') ouvrir(c); }}>
                    <td><span className="rh-cellule-principale">{c.salarie}</span><span className="rh-cellule-secondaire" style={{ display: 'block' }}>{c.reference} · {c.matricule}</span></td>
                    <td>{libelle(ORIGINES, c.origine_type)} <BadgeSimulation actif={c.simulation} /></td>
                    <td style={{ textAlign: 'right' }}><Montant n={c.montant_initial} /></td>
                    <td style={{ textAlign: 'right' }}><Montant n={c.solde} fort /></td>
                    <td><Etat table={ETATS_CREANCE} code={c.etat} />{c.mouvements_a_valider > 0 && <div style={{ marginTop: 4 }}><Badge ton="alerte">{c.mouvements_a_valider} à valider</Badge></div>}</td>
                    <td style={{ width: 32, color: 'var(--sg-texte-4)' }}><ChevronRight size={16} aria-hidden="true" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationDs donnees={liste.donnees} onPage={setPage} />
        </EtatsLecture>
      </section>
    </>
  );
}

// ─── Paiements ─────────────────────────────────────────────────────────────────────────────────
/** Opérations préparées depuis la trésorerie (les prêts et les aides se paient depuis leur fiche). */
const OPERATIONS_LIBRES = {
  FRAIS_BANCAIRES: { sens: 'DECAISSEMENT', objet_type: 'AUTRE', libelle: 'Frais bancaires (décaissement)' },
  SUBVENTION_RECUE: { sens: 'ENCAISSEMENT', objet_type: 'SUBVENTION', libelle: 'Subvention reçue (encaissement)' },
  VIREMENT_RETENUES: { sens: 'ENCAISSEMENT', objet_type: 'VIREMENT_RETENUES', libelle: 'Virement des retenues (encaissement)' },
};

function FenetrePreparerPaiement({ organisation, onFermer, onFait }) {
  const api = useSocial();
  const treso = useLectureSociale(() => api.tresorerie(organisation.id), [api, organisation.id]);
  const comptes = toArray(treso.donnees?.comptes).filter((c) => c.etat === 'ACTIF');
  const [operation, setOperation] = useState('FRAIS_BANCAIRES');
  const [choix, setChoix] = useState(choixInitial);
  const [montant, setMontant] = useState('');
  const [lib, setLib] = useState('');
  const [date, setDate] = useState(aujourdhuiDakar());
  const [reference, setReference] = useState('');
  const ch = lireChoix(comptes, choix);
  const m = lireMontant(montant);
  const op = OPERATIONS_LIBRES[operation];
  return (
    <FenetreActe titre="Préparer un paiement" Icone={Banknote} libelleBouton="Préparer" pret={!!(paiementComplet(ch) && m && lib.trim().length >= 3 && date)} onFermer={onFermer}
      aide="Les décaissements de prêts et d'aides se préparent depuis leur fiche. Un paiement préparé est ensuite signé, contresigné et exécuté par d'autres personnes."
      onConfirmer={async (cle) => {
        await api.preparerPaiement(organisation.id, {
          sens: op.sens, objet_type: op.objet_type, type_operation: operation, libelle: lib.trim(), montant: m, date_valeur: date, ...champsPaiement(ch),
          ...(reference.trim() ? { reference: reference.trim() } : {}),
        }, cle);
        onFait('Paiement préparé.');
      }}>
      <Champ libelle="Opération" requis>
        <select className="rh-champ-saisie" value={operation} onChange={(e) => setOperation(e.target.value)}>
          {Object.entries(OPERATIONS_LIBRES).map(([k, o]) => <option key={k} value={k}>{o.libelle}</option>)}
        </select>
      </Champ>
      <ChoixCompteMode comptes={comptes} lecture={treso} valeur={choix} onChange={setChoix} />
      <Champ libelle="Libellé" requis><input className="rh-champ-saisie" maxLength={200} value={lib} onChange={(e) => setLib(e.target.value)} /></Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        <Champ libelle="Montant (F)" requis><input className="rh-champ-saisie" inputMode="numeric" value={montant} onChange={(e) => setMontant(e.target.value)} /></Champ>
        <Champ libelle="Date de valeur" requis><input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} /></Champ>
      </div>
      <Champ libelle="Référence" aide="Facultative."><input className="rh-champ-saisie" maxLength={80} value={reference} onChange={(e) => setReference(e.target.value)} /></Champ>
    </FenetreActe>
  );
}

function Paiements({ organisation, droits }) {
  const api = useSocial();
  const naviguer = useNavigate();
  const [statut, setStatut] = useState('');
  const [page, setPage] = useState(1);
  const [preparer, setPreparer] = useState(false);
  const [info, setInfo] = useState('');
  const liste = useLectureSociale(() => api.paiements(organisation.id, { page, taille: 25, statut }), [api, organisation.id, page, statut]);
  const elements = toArray(liste.donnees);
  const ouvrir = (p) => naviguer(`/dispositifs/${organisation.id}/tresorerie/paiements/${p.id}`);
  return (
    <>
      <div className="rh-outils" style={{ justifyContent: 'space-between' }}>
        <FiltreEtats table={ETATS_PAIEMENT} codes={Object.keys(ETATS_PAIEMENT)} valeur={statut} onChange={(c) => { setStatut(c); setPage(1); }} />
        {droits.a('ds_paiement_preparer') && <Bouton variante="primaire" Icone={Plus} onClick={() => setPreparer(true)}>Préparer un paiement</Bouton>}
      </div>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={liste} Icone={Banknote} estVide={!elements.length} titreVide={statut ? 'Aucun paiement dans cet état' : 'Aucun paiement'}
          vide={statut ? 'Choisissez un autre état.' : 'Les décaissements (prêts, aides) et les encaissements préparés apparaissent ici, avec leurs signatures.'}>
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Paiement</th><th>Objet</th><th style={{ textAlign: 'right' }}>Montant</th><th>Mode</th><th>État</th><th aria-label="Ouvrir" /></tr></thead>
              <tbody>
                {elements.map((p) => (
                  <tr key={p.id} className="rh-ligne" tabIndex={0} onClick={() => ouvrir(p)} onKeyDown={(e) => { if (e.key === 'Enter') ouvrir(p); }}>
                    <td><span className="rh-cellule-principale">{p.libelle}</span><span className="rh-cellule-secondaire" style={{ display: 'block' }}>{p.numero || 'Numéro attribué à l\'exécution'} · valeur au {dateLongue(String(p.date_valeur || '').slice(0, 10))}</span></td>
                    <td>{libelle(OBJETS_PAIEMENT, p.objet_type)}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{p.sens === 'ENCAISSEMENT' ? 'Encaissement' : 'Décaissement'}{p.beneficiaire ? ` · ${p.beneficiaire}` : ''}</span></td>
                    <td style={{ textAlign: 'right' }}><Montant n={p.montant} /></td>
                    <td>{libelle(MODES_PAIEMENT, p.mode)}</td>
                    <td><Etat table={ETATS_PAIEMENT} code={p.statut} />{p.statut === 'SIGNE' && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{p.second_signataire_id ? `contresigné${p.second_signataire_nom ? ` par ${p.second_signataire_nom}` : ''}` : 'à contresigner'}</span>}
                      {p.prepare_par_nom && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>préparé par {p.prepare_par_nom}</span>}<LigneSuivi suivi={p.suivi} /></td>
                    <td style={{ width: 32, color: 'var(--sg-texte-4)' }}><ChevronRight size={16} aria-hidden="true" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationDs donnees={liste.donnees} onPage={setPage} />
        </EtatsLecture>
      </section>
      {preparer && <FenetrePreparerPaiement organisation={organisation} onFermer={() => setPreparer(false)} onFait={(m) => { setPreparer(false); setInfo(m); liste.recharger(); }} />}
    </>
  );
}

// ─── Comptes, caisse, rapprochements ──────────────────────────────────────────────────────────
function FenetreArrete({ organisation, compte, onFermer, onFait }) {
  const api = useSocial();
  const piece = usePieceDepot(organisation.id, 'AUTRE', null, 'PV');
  const [date, setDate] = useState(aujourdhuiDakar());
  const [billets, setBillets] = useState({});
  const total = COUPURES.reduce((s, c) => s + c * (Number(billets[c]) || 0), 0);
  return (
    <FenetreActe titre={`Arrêté de caisse — ${compte.libelle}`} Icone={Wallet} libelleBouton="Établir l'arrêté" pret={!!(date && piece.prete)} onFermer={onFermer} large
      aide="Comptez les billets et pièces. L'écart avec le livre est constaté par une écriture, jamais ajusté ; une autre personne contrôle ensuite l'arrêté."
      onConfirmer={async (cle) => {
        const p = await piece.deposer();
        const billetage = Object.fromEntries(COUPURES.filter((c) => Number(billets[c]) > 0).map((c) => [String(c), Number(billets[c])]));
        const r = await api.etablirArrete(compte.id, { date, billetage, piece_id: p.id }, cle);
        onFait(`Arrêté établi : compté ${francs(r.solde_compte)}, livre ${francs(r.solde_theorique)}, écart ${francs(r.ecart)}. Il attend le contrôle d'une autre personne.`);
      }}>
      <Champ libelle="Date de l'arrêté" requis><input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} /></Champ>
      <fieldset style={{ border: '1px solid var(--sg-bord)', borderRadius: 6, padding: '10px 12px', margin: '0 0 12px' }}>
        <legend style={{ fontSize: 13, fontWeight: 500, color: 'var(--sg-texte-2)', padding: '0 4px' }}>Billetage (nombre par coupure)</legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
          {COUPURES.map((c) => (
            <label key={c} style={{ fontSize: 13 }}>
              <span style={{ display: 'block', color: 'var(--sg-texte-2)', marginBottom: 2 }}>{francs(c)}</span>
              <input className="rh-champ-saisie" inputMode="numeric" value={billets[c] || ''} onChange={(e) => setBillets((b) => ({ ...b, [c]: e.target.value.replace(/\D/g, '') }))} />
            </label>
          ))}
        </div>
        <p style={{ margin: '10px 0 0', fontSize: 14 }}>Total compté : <strong>{francs(total)}</strong></p>
      </fieldset>
      <ChampPiece piece={piece} types={['PV', 'JUSTIFICATIF']} libelleChamp="Procès-verbal d'arrêté" />
    </FenetreActe>
  );
}

function FenetreRapprochement({ organisation, compte, onFermer, onFait }) {
  const api = useSocial();
  const piece = usePieceDepot(organisation.id, 'AUTRE', null, 'RELEVE');
  const [mois, setMois] = useState(periodeDecalee(periodeCourante(), -1));
  const [solde, setSolde] = useState('');
  const [suspens, setSuspens] = useState([]);
  const s = /^-?\d{1,12}$/.test(String(solde).replace(/\s/g, '')) ? Number(String(solde).replace(/\s/g, '')) : null;
  const suspensOk = suspens.every((x) => x.libelle.trim().length >= 3 && lireMontant(x.montant));
  return (
    <FenetreActe titre={`Rapprochement bancaire — ${compte.libelle}`} Icone={Scale} libelleBouton="Établir le rapprochement" pret={!!(mois && s !== null && suspensOk && piece.prete)} onFermer={onFermer}
      aide="Solde du relevé, opérations en suspens et relevé en pièce jointe. Une autre personne, qui n'a pas décaissé sur ce compte dans le mois, contrôle ensuite."
      onConfirmer={async (cle) => {
        const p = await piece.deposer();
        const r = await api.etablirRapprochement(compte.id, { mois, solde_releve: s, suspens: suspens.map((x) => ({ libelle: x.libelle.trim(), sens: Number(x.sens), montant: lireMontant(x.montant) })), piece_id: p.id }, cle);
        onFait(`Rapprochement de ${moisLong(mois)} établi : écart ${francs(r.ecart)}. Il attend le contrôle d'une autre personne.`);
      }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <Champ libelle="Mois" requis><input type="month" className="rh-champ-saisie" value={mois} onChange={(e) => setMois(e.target.value)} /></Champ>
        <Champ libelle="Solde du relevé (F)" requis><input className="rh-champ-saisie" inputMode="numeric" value={solde} onChange={(e) => setSolde(e.target.value)} /></Champ>
      </div>
      <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 500, color: 'var(--sg-texte-2)' }}>Opérations en suspens</p>
      {suspens.map((x, i) => (
        <div key={x.cle} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) auto', gap: 6, marginBottom: 6 }}>
          <input className="rh-champ-saisie" aria-label="Libellé du suspens" placeholder="Libellé" value={x.libelle} onChange={(e) => setSuspens((l) => l.map((y, j) => (j === i ? { ...y, libelle: e.target.value } : y)))} />
          <select className="rh-champ-saisie" aria-label="Sens" value={x.sens} onChange={(e) => setSuspens((l) => l.map((y, j) => (j === i ? { ...y, sens: e.target.value } : y)))}>
            <option value="1">Au crédit</option><option value="-1">Au débit</option>
          </select>
          <input className="rh-champ-saisie" aria-label="Montant" inputMode="numeric" placeholder="Montant" value={x.montant} onChange={(e) => setSuspens((l) => l.map((y, j) => (j === i ? { ...y, montant: e.target.value } : y)))} />
          <Bouton petit variante="discret" onClick={() => setSuspens((l) => l.filter((_, j) => j !== i))}>Retirer</Bouton>
        </div>
      ))}
      <div style={{ marginBottom: 12 }}><Bouton petit Icone={Plus} onClick={() => setSuspens((l) => [...l, { cle: `${Date.now()}-${l.length}`, libelle: '', sens: '1', montant: '' }])}>Ajouter un suspens</Bouton></div>
      <ChampPiece piece={piece} types={['RELEVE']} libelleChamp="Relevé bancaire" />
    </FenetreActe>
  );
}


// ─── Chèques d'un compte ───────────────────────────────────────────────────────────────────────
function FenetreChequier({ compte, onFermer, onFait }) {
  const api = useSocial();
  const [chequier, setChequier] = useState('');
  const [premier, setPremier] = useState('');
  const [nombre, setNombre] = useState('25');
  const p = /^\d{1,12}$/.test(premier.trim()) ? Number(premier.trim()) : null;
  const n = /^\d{1,3}$/.test(nombre.trim()) ? Number(nombre.trim()) : null;
  const refOk = chequier.trim().length >= 1 && chequier.trim().length <= 20;
  return (
    <FenetreActe titre={`Enregistrer un chéquier — ${compte.libelle}`} Icone={Plus} libelleBouton="Enregistrer" pret={!!(refOk && p !== null && p >= 1 && n && n <= 100)} onFermer={onFermer}
      aide="Chaque formule devient un chèque DISPONIBLE, à choisir lors de la préparation d'un paiement par chèque."
      onConfirmer={async (cle) => { const r = await api.enregistrerChequier(compte.id, { chequier: chequier.trim(), premier: p, nombre: n }, cle); onFait(`Chéquier ${r?.chequier || chequier.trim()} enregistré : ${toArray(r?.cheques).length || n} chèque(s) disponible(s).`); }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        <Champ libelle="Référence du chéquier" requis aide="20 caractères au plus."><input className="rh-champ-saisie" maxLength={20} value={chequier} onChange={(e) => setChequier(e.target.value)} /></Champ>
        <Champ libelle="Premier numéro" requis><input className="rh-champ-saisie" inputMode="numeric" value={premier} onChange={(e) => setPremier(e.target.value)} /></Champ>
        <Champ libelle="Nombre de formules" requis aide="100 au plus."><input className="rh-champ-saisie" inputMode="numeric" value={nombre} onChange={(e) => setNombre(e.target.value)} /></Champ>
      </div>
    </FenetreActe>
  );
}

function FenetreStatutCheque({ cheque, vers, onFermer, onFait }) {
  const api = useSocial();
  const [motif, setMotif] = useState('');
  const titre = vers === 'ANNULE' ? `Annuler le chèque n° ${cheque.numero}` : `Opposition sur le chèque n° ${cheque.numero}`;
  return (
    <FenetreActe titre={titre} Icone={Ban} libelleBouton={vers === 'ANNULE' ? 'Annuler le chèque' : 'Déclarer perdu ou volé'} danger pret={motifValide(motif, 3)} onFermer={onFermer}
      aide={vers === 'ANNULE' ? 'Un chèque vierge s\'annule (la souche est conservée). Un chèque lié à un paiement exécuté s\'annule par la contrepassation de l\'écriture.' : 'Déclarez l\'opposition auprès de la banque ; le chèque ne pourra plus être utilisé.'}
      onConfirmer={async (cle) => { await api.changerCheque(cheque.id, { vers, motif: motif.trim() }, cle); onFait(vers === 'ANNULE' ? 'Chèque annulé.' : 'Opposition enregistrée.'); }}>
      <ChampMotif valeur={motif} onChange={setMotif} aide="3 caractères au moins. Le motif est conservé dans l'historique." />
    </FenetreActe>
  );
}

function FenetreCheques({ compte, droits, onFermer }) {
  const api = useSocial();
  const [statut, setStatut] = useState('');
  const [page, setPage] = useState(1);
  const [version, setVersion] = useState(0);
  const [acte, setActe] = useState(null);
  const [info, setInfo] = useState('');
  const l = useLectureSociale(() => api.cheques(compte.id, { statut, page, taille: 25 }), [api, compte.id, statut, page, version]);
  const cheques = toArray(l.donnees);
  const gerer = droits.a('ds_cheque_gerer');
  const fait = (m) => { setActe(null); setInfo(m); setVersion((v) => v + 1); };
  return (
    <Fenetre titre={`Chèques — ${compte.libelle}`} Icone={ListChecks} onFermer={onFermer} large>
      <div className="rh-outils" style={{ justifyContent: 'space-between' }}>
        <FiltreEtats table={STATUTS_CHEQUE} codes={Object.keys(STATUTS_CHEQUE)} valeur={statut} onChange={(c) => { setStatut(c); setPage(1); }} libelleGroupe="Filtrer les chèques" />
        {gerer && <Bouton petit Icone={Plus} onClick={() => setActe({ type: 'chequier' })}>Enregistrer un chéquier</Bouton>}
      </div>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <EtatsLecture lecture={l} Icone={ListChecks} estVide={!cheques.length} titreVide={statut ? 'Aucun chèque dans cet état' : 'Aucun chèque'}
        vide={statut ? 'Choisissez un autre état.' : 'Enregistrez un chéquier pour pouvoir payer par chèque.'}>
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Chéquier</th><th>Numéro</th><th>État</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {cheques.map((c) => (
                <tr key={c.id}>
                  <td>{c.chequier}</td><td style={{ fontVariantNumeric: 'tabular-nums' }}>{c.numero}</td><td><Etat table={STATUTS_CHEQUE} code={c.statut} /></td>
                  <td style={{ textAlign: 'right' }}>
                    {gerer && c.statut === 'DISPONIBLE' && <Bouton petit variante="discret" onClick={() => setActe({ type: 'statut', c, vers: 'ANNULE' })}>Annuler</Bouton>}
                    {gerer && ['DISPONIBLE', 'EMIS', 'REMIS'].includes(c.statut) && <Bouton petit variante="discret" onClick={() => setActe({ type: 'statut', c, vers: 'PERDU_VOLE' })}>Opposition</Bouton>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationDs donnees={l.donnees} onPage={setPage} />
      </EtatsLecture>
      {acte?.type === 'chequier' && <FenetreChequier compte={compte} onFermer={() => setActe(null)} onFait={fait} />}
      {acte?.type === 'statut' && <FenetreStatutCheque cheque={acte.c} vers={acte.vers} onFermer={() => setActe(null)} onFait={fait} />}
    </Fenetre>
  );
}

// ─── Arrêtés et rapprochements : liste et contrôle par une autre personne ─────────────────────
function FenetreControles({ compte, genre, droits, onFermer, onControle }) {
  const api = useSocial();
  const arretes = genre === 'arretes';
  const [page, setPage] = useState(1);
  const [version, setVersion] = useState(0);
  const [erreur, setErreur] = useState(null);
  const [info, setInfo] = useState('');
  const [enCours, setEnCours] = useState(null);
  const [cles] = useState(() => ({}));
  const l = useLectureSociale(() => (arretes ? api.arretes(compte.id, { page }) : api.rapprochements(compte.id, { page })), [api, compte.id, arretes, page, version]);
  const lignes = toArray(l.donnees);
  const peutControler = droits.a('ds_rapprochement_controler');
  const controler = async (x) => {
    setEnCours(x.id); setErreur(null); setInfo('');
    cles[x.id] = cles[x.id] || nouvelleCle();
    try {
      const r = await (arretes ? api.controlerArrete(x.id, cles[x.id]) : api.controlerRapprochement(x.id, cles[x.id]));
      setInfo(`${arretes ? 'Arrêté' : 'Rapprochement'} contrôlé${r && r.ecriture && r.ecriture.numero ? ` : écart constaté par l'écriture ${r.ecriture.numero}` : ''}.`);
      setVersion((v) => v + 1);
      if (onControle) onControle();
    } catch (e) {
      if (cleARenouveler(e)) delete cles[x.id]; // refus 4xx éventuellement mémorisé : nouvel essai = nouvelle clé
      setErreur(erreurSociale(e));
    }
    setEnCours(null);
  };
  return (
    <Fenetre titre={`${arretes ? 'Arrêtés de caisse' : 'Rapprochements bancaires'} — ${compte.libelle}`} Icone={BookCheck} onFermer={onFermer} large>
      <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
        Le contrôle se fait par une autre personne que celle qui a établi {arretes ? 'l\'arrêté' : 'le rapprochement'}{arretes ? '' : ', et qui n\'a pas décaissé sur ce compte dans le mois'}.{arretes ? ' Un écart est constaté par une écriture, jamais ajusté.' : ''}
      </p>
      <ErreurServeur erreur={erreur} />
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <EtatsLecture lecture={l} Icone={BookCheck} estVide={!lignes.length} titreVide={arretes ? 'Aucun arrêté' : 'Aucun rapprochement'} vide="Rien n'a encore été établi sur ce compte.">
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>{arretes ? 'Date' : 'Mois'}</th><th style={{ textAlign: 'right' }}>Écart</th><th>Établi par</th><th>Contrôle</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {lignes.map((x) => (
                <tr key={x.id}>
                  <td>{arretes ? dateLongue(x.date) : moisLong(x.mois)}</td>
                  <td style={{ textAlign: 'right' }}><Montant n={x.ecart} /></td>
                  <td>{x.etabli_par || '—'}</td>
                  <td>{x.controle ? <><Badge ton="succes" point>Contrôlé</Badge>{x.controle_par && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>par {x.controle_par}</span>}</> : <Badge ton="alerte" point>À contrôler</Badge>}</td>
                  <td style={{ textAlign: 'right' }}>{!x.controle && peutControler && <Bouton petit variante="primaire" disabled={enCours === x.id} onClick={() => controler(x)}>Contrôler</Bouton>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationDs donnees={l.donnees} onPage={setPage} />
      </EtatsLecture>
    </Fenetre>
  );
}

function CarteCompte({ compte, droits, onArrete, onRapprochement, onCheques, onControles }) {
  const caisse = compte.type === 'CAISSE';
  const dernier = caisse ? compte.dernier_arrete : compte.dernier_rapprochement;
  const aControler = !!(dernier && !dernier.controle);
  return (
    <section className="rh-carte">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>{caisse ? <Wallet size={16} aria-hidden="true" /> : <Landmark size={16} aria-hidden="true" />}{compte.libelle}</h3>
          <p className="rh-cellule-secondaire" style={{ margin: '2px 0 0' }}>{caisse ? 'Caisse' : `Banque${compte.banque ? ` · ${compte.banque}` : ''}${compte.rib_masque ? ` · ${compte.rib_masque}` : ''}`} · compte {compte.compte_numero}{compte.etat === 'CLOS' ? ' · clos' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {caisse && droits.a('ds_caisse_arreter') && <Bouton petit Icone={Wallet} onClick={onArrete}>Arrêter la caisse</Bouton>}
          {!caisse && droits.a('ds_rapprochement_etablir') && <Bouton petit Icone={Scale} onClick={onRapprochement}>Rapprochement</Bouton>}
          {!caisse && <Bouton petit Icone={ListChecks} onClick={onCheques}>Chèques</Bouton>}
          <Bouton petit Icone={BookCheck} onClick={onControles}>{caisse ? 'Arrêtés' : 'Rapprochements'}{aControler ? ' (à contrôler)' : ''}</Bouton>
        </div>
      </div>
      <Infos lignes={[
        ['Solde du livre', <Montant n={compte.solde_livre} fort />], ['Paiements en cours', String(compte.paiements_en_cours ?? 0)],
        ['Total décaissé', <Montant n={compte.total_decaisse} />], ['Total encaissé', <Montant n={compte.total_encaisse} />],
        ['Chèques', compte.cheques ? `${compte.cheques.disponibles} disponible(s), ${compte.cheques.emis_non_debites} émis non débité(s)` : null],
        ['Dernier arrêté', compte.dernier_arrete ? `${dateLongue(compte.dernier_arrete.date)} · écart ${francs(compte.dernier_arrete.ecart)} · ${compte.dernier_arrete.controle ? 'contrôlé' : 'à contrôler'}` : (caisse ? 'Aucun' : null)],
        ['Dernier rapprochement', compte.dernier_rapprochement ? `${moisLong(compte.dernier_rapprochement.mois)} · écart ${francs(compte.dernier_rapprochement.ecart)} · ${compte.dernier_rapprochement.controle ? 'contrôlé' : 'à contrôler'}` : (!caisse ? 'Aucun' : null)],
      ]} />
    </section>
  );
}

function Comptes({ organisation, droits }) {
  const api = useSocial();
  const t = useLectureSociale(() => api.tresorerie(organisation.id), [api, organisation.id]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const comptes = toArray(t.donnees?.comptes);
  const fait = (m) => { setFenetre(null); setInfo(m); t.recharger(); };
  return (
    <>
      {droits.a('ds_ecriture_saisir') && t.donnees && (
        <div className="rh-outils" style={{ justifyContent: 'flex-end' }}>
          <Bouton variante={comptes.length ? 'secondaire' : 'primaire'} Icone={Plus} onClick={() => { setInfo(''); setFenetre({ type: 'nouveau' }); }}>Créer un compte de trésorerie</Bouton>
        </div>
      )}
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      {(!t.donnees || !comptes.length) ? (
        <section className="rh-carte" style={{ padding: 0 }}>
          <EtatsLecture lecture={t} Icone={Landmark} estVide={!comptes.length} titreVide="Aucun compte de trésorerie"
            vide={droits.a('ds_ecriture_saisir') ? 'Créez le compte bancaire et la caisse de démonstration, après l\'installation du plan (Comptabilité › Mise en place).'
              : 'Les comptes (banque, caisse) sont créés par le trésorier après l\'installation du plan de démonstration.'}>{null}</EtatsLecture>
        </section>
      ) : comptes.map((c) => (
        <CarteCompte key={c.id} compte={c} droits={droits} onArrete={() => setFenetre({ type: 'arrete', compte: c })} onRapprochement={() => setFenetre({ type: 'rappro', compte: c })}
          onCheques={() => setFenetre({ type: 'cheques', compte: c })} onControles={() => setFenetre({ type: 'controles', compte: c })} />
      ))}
      <p className="rh-form-aide">Le contrôle d'un arrêté ou d'un rapprochement se fait par une autre personne que celle qui l'a établi (bouton « Arrêtés » ou « Rapprochements » du compte).</p>
      {fenetre?.type === 'nouveau' && <FenetreCompteTresorerie organisation={organisation} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'arrete' && <FenetreArrete organisation={organisation} compte={fenetre.compte} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'rappro' && <FenetreRapprochement organisation={organisation} compte={fenetre.compte} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'cheques' && <FenetreCheques compte={fenetre.compte} droits={droits} onFermer={() => { setFenetre(null); t.recharger(); }} />}
      {fenetre?.type === 'controles' && <FenetreControles compte={fenetre.compte} genre={fenetre.compte.type === 'CAISSE' ? 'arretes' : 'rapprochements'} droits={droits} onFermer={() => setFenetre(null)} onControle={t.recharger} />}
    </>
  );
}

const VUES = [['creances', 'Créances'], ['paiements', 'Paiements'], ['comptes', 'Comptes et caisse']];

export default function TresorerieSection({ organisation, droits }) {
  const [params] = useSearchParams();
  const [vue, setVue] = useState(() => (VUES.some(([c]) => c === params.get('vue')) ? params.get('vue') : 'creances'));
  return (
    <>
      <BandeauComptaDemo />
      <div className="rh-vues rh-vues-ligne" role="group" aria-label="Vue de la trésorerie">
        {VUES.map(([c, l]) => <button key={c} type="button" className="rh-vue" aria-pressed={vue === c} onClick={() => setVue(c)}>{l}</button>)}
      </div>
      {vue === 'creances' && <Creances organisation={organisation} />}
      {vue === 'paiements' && <Paiements organisation={organisation} droits={droits} />}
      {vue === 'comptes' && <Comptes organisation={organisation} droits={droits} />}
    </>
  );
}
